import { useState } from "react";
import { fetchAbsoluteWithAuth } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { API_BASE_URL } from "@/lib/constants";
import { toastHandler } from "@/lib/ToastHandler";

export interface ManualAssignmentPayload {
  order_id: number;
  driver_id: number;
  user_id: number;
}

export interface AvailableDriver {
  id: number;
  name: string | null;
  branch_id: number | null;
  vehicle_type: string | null;
  worker_type: string | null;
  picture?: string | null;
}
const DRIVERS_SERVICE_URL = `${API_BASE_URL}/driver`;

const ASSIGNMENT_SERVICE_URL = `${API_BASE_URL}/assignment`;

export function useAssignation() {
  const { getAccessToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Per-operation states
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [driversByBranchLoading, setDriversByBranchLoading] = useState(false);
  const [driversByBranchError, setDriversByBranchError] = useState<
    string | null
  >(null);
  const [driversByOrderLoading, setDriversByOrderLoading] = useState(false);
  const [driversByOrderError, setDriversByOrderError] = useState<string | null>(
    null,
  );
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const postManualAssignment = async ({
    order_id,
    driver_id,
    user_id,
  }: ManualAssignmentPayload) => {
    setLoading(true);
    setError(null);
    setAssignLoading(true);
    setAssignError(null);
    try {
      const token = await getAccessToken();
      const endpoint = `${ASSIGNMENT_SERVICE_URL}/assignments/manual`;
      const response = await fetchAbsoluteWithAuth(endpoint, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order_id, driver_id, user_id }),
      });
      return response;
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Error creando asignación";
      setError(message);
      setAssignError(message);
      throw err;
    } finally {
      setLoading(false);
      setAssignLoading(false);
    }
  };

  const getAvailableDriversByBranch = async (
    branchId: number,
  ): Promise<AvailableDriver[]> => {
    setLoading(true);
    setError(null);
    setDriversByBranchLoading(true);
    setDriversByBranchError(null);
    try {
      const token = await getAccessToken();
      const endpoint = `${DRIVERS_SERVICE_URL}/drivers/assignment-available/branch/${branchId}`;
      const response = await fetchAbsoluteWithAuth(endpoint, token);
      return Array.isArray(response) ? response : response?.data || [];
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Error obteniendo conductores";
      setError(message);
      setDriversByBranchError(message);
      throw err;
    } finally {
      setLoading(false);
      setDriversByBranchLoading(false);
    }
  };

  const getAvailableDriversByOrder = async (
    orderId: number,
  ): Promise<AvailableDriver[]> => {
    setLoading(true);
    setError(null);
    setDriversByOrderLoading(true);
    setDriversByOrderError(null);
    try {
      const token = await getAccessToken();
      const endpoint = `${DRIVERS_SERVICE_URL}/drivers/assignment-available/order/${orderId}`;
      const response = await fetchAbsoluteWithAuth(endpoint, token);
      return Array.isArray(response) ? response : response?.data || [];
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Error obteniendo conductores";
      setError(message);
      setDriversByOrderError(message);
      toastHandler({
        description: message,
        variant: "destructive",
        title: "Error",
      });
      return [];
    } finally {
      setLoading(false);
      setDriversByOrderLoading(false);
    }
  };

  const cancelAssignment = async (
    orderId: number | string,
    user_id: number,
  ) => {
    setLoading(true);
    setError(null);
    setCancelLoading(true);
    setCancelError(null);
    try {
      const token = await getAccessToken();
      const endpoint = `${ASSIGNMENT_SERVICE_URL}/assignments/order/${orderId}/cancel`;
      const response = await fetchAbsoluteWithAuth(endpoint, token, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id }),
      });
      return response;
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Error cancelando asignación";
      setError(message);
      setCancelError(message);
      throw err;
    } finally {
      setLoading(false);
      setCancelLoading(false);
    }
  };

  return {
    loading,
    error,
    assignLoading,
    assignError,
    driversByBranchLoading,
    driversByBranchError,
    driversByOrderLoading,
    driversByOrderError,
    cancelLoading,
    cancelError,
    postManualAssignment,
    getAvailableDriversByBranch,
    getAvailableDriversByOrder,
    cancelAssignment,
  };
}
